import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { reportsService } from '../services';
import { format, parseISO } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  TrendingUp, CheckCircle, AlertTriangle, Trophy,
  Building2, Loader2, ChevronRight, Calendar,
  ArrowUpRight, ArrowDownRight, BarChart3, Bell, Info,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PCDrilldownDialog from '../components/reports/PCDrilldownDialog';

// ─── tiny helpers ──────────────────────────────────────────────────────────────

function fmtMonth(isoMonth) {
  try { return format(parseISO(isoMonth + '-01'), 'MMM'); }
  catch { return isoMonth; }
}

function fmtMonthFull(isoMonth) {
  try { return format(parseISO(isoMonth + '-01'), 'MMM yyyy'); }
  catch { return isoMonth; }
}

function currentMonthISO() {
  return format(new Date(), 'yyyy-MM');
}

function ChangeChip({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
      up ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
    }`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(pct)}%
    </span>
  );
}

// KPI tile (left-accent style)
function KPITile({ label, value, sub, color = 'slate', testId, tooltip, onClick }) {
  const palette = {
    emerald: 'border-l-emerald-400 bg-emerald-50/70',
    blue:    'border-l-blue-400 bg-blue-50/70',
    orange:  'border-l-orange-400 bg-orange-50/70',
    red:     'border-l-red-500 bg-red-50/70',
    indigo:  'border-l-indigo-400 bg-indigo-50/70',
    slate:   'border-l-slate-300 bg-slate-50/70',
  };
  const interactive = !!onClick;
  return (
    <div
      className={`border-l-4 rounded-r-lg px-3 py-2.5 ${palette[color] ?? palette.slate} ${
        interactive ? 'cursor-pointer hover:brightness-95 active:brightness-90 transition-all' : ''
      }`}
      data-testid={testId}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="flex items-center gap-1">
        <p className="text-[11px] text-slate-500 font-medium leading-snug">{label}</p>
        {tooltip && (
          <div className="group relative shrink-0">
            <Info className="h-3 w-3 text-slate-400 cursor-help" />
            <div className="absolute top-5 left-1/2 -translate-x-1/2 hidden group-hover:block z-30 w-52 rounded-md bg-slate-800 text-white text-[10px] px-2.5 py-1.5 shadow-lg pointer-events-none leading-relaxed">
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800" />
              {tooltip}
            </div>
          </div>
        )}
        {interactive && <ChevronRight className="h-3 w-3 text-slate-400 ml-auto shrink-0" />}
      </div>
      <p className="text-xl font-bold text-slate-800 leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// Section wrapper with coloured header + drill-through links
function Section({ id, icon: Icon, title, accent, links, children }) {
  const hdr = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-800',
    orange:  'bg-orange-50 border-orange-200 text-orange-800',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-800',
  }[accent] ?? 'bg-slate-50 border-slate-200 text-slate-800';
  const navigate = useNavigate();
  return (
    <Card data-testid={`section-${id}`} className="overflow-hidden">
      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${hdr}`}>
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Icon className="h-4 w-4 shrink-0" />
          {title}
        </div>
        <div className="flex items-center gap-3">
          {links.map((l, idx) => (
            <button
              key={`${l.to}-${idx}`}
              onClick={() => navigate(l.to)}
              className="text-xs flex items-center gap-0.5 opacity-70 hover:opacity-100 transition-opacity"
              data-testid={`section-${id}-link-${l.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {l.label} <ChevronRight className="h-3 w-3" />
            </button>
          ))}
        </div>
      </div>
      <CardContent className="pt-4 pb-4 space-y-4">{children}</CardContent>
    </Card>
  );
}

// Ageing bucket bar
function AgeingRow({ label, count, total, color, onClick }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const hasData = count > 0;
  return (
    <div
      className={`flex items-center gap-2 text-xs ${hasData && onClick ? 'cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 transition-colors' : ''}`}
      data-testid={`ageing-row-${label.replace(/\W/g, '')}`}
      onClick={hasData && onClick ? onClick : undefined}
      role={hasData && onClick ? 'button' : undefined}
    >
      <span className="w-10 text-right text-slate-500 shrink-0">{label}</span>
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          style={{ width: `${pct}%`, backgroundColor: color }}
          className="h-full rounded-full transition-all duration-500"
        />
      </div>
      <span className="w-6 text-slate-700 font-semibold shrink-0">{count}</span>
      {hasData && onClick && <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />}
    </div>
  );
}

// Custom tooltip for revenue bar chart
function RevenueTooltip({ active, payload, label, formatCurrency }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{fmtMonthFull(d?.rawMonth)}</p>
      <p className="text-emerald-700">{formatCurrency(d?.amount ?? 0, 0)} completed</p>
      <p className="text-slate-500">{d?.job_count} job{d?.job_count !== 1 ? 's' : ''}</p>
      {d?.isCurrent && <p className="text-amber-600 mt-1 font-medium">In Progress</p>}
    </div>
  );
}

// Custom tooltip for FTA line chart
function FTATooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{fmtMonthFull(d?.rawMonth)}</p>
      <p className="text-indigo-700">{d?.rate?.toFixed(1)}% first-time approval</p>
      <p className="text-slate-500">
        {d?.fta}/{d?.total} published{d?.total === 0 ? ' — no data' : ''}
      </p>
      {d?.isCurrent && <p className="text-amber-600 mt-1 font-medium">In Progress</p>}
    </div>
  );
}

// ─── Period Selector ───────────────────────────────────────────────────────────

function PeriodSelector({ period, setPeriod, pendingFrom, setPendingFrom, pendingTo, setPendingTo, onApply }) {
  const tabs = [
    { key: 'ytd',     label: 'YTD' },
    { key: 'last_12m', label: 'Last 12M' },
    { key: 'custom',  label: 'Custom' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="period-selector">
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setPeriod(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              period === t.key
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-800'
            }`}
            data-testid={`period-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="custom-date-inputs">
          <input
            type="date"
            value={pendingFrom}
            onChange={e => setPendingFrom(e.target.value)}
            className="h-9 rounded-md border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
            data-testid="custom-from-date"
          />
          <span className="text-slate-400 text-sm">→</span>
          <input
            type="date"
            value={pendingTo}
            onChange={e => setPendingTo(e.target.value)}
            className="h-9 rounded-md border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
            data-testid="custom-to-date"
          />
          <Button
            size="sm"
            variant="default"
            onClick={onApply}
            disabled={!pendingFrom || !pendingTo}
            data-testid="custom-apply-btn"
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function PerformanceCentrePage() {
  const { isAdmin, isScheduler } = useAuth();
  const { formatCurrency, region } = useSettings();
  const navigate = useNavigate();

  const [period, setPeriod]         = useState('ytd');
  const [fromDate, setFromDate]     = useState('');
  const [toDate, setToDate]         = useState('');
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo]   = useState('');
  const [data, setData]             = useState(null);
  const [pulseData, setPulseData]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [ftaMethod, setFtaMethod]   = useState('formal'); // 'formal' | 'all'

  // Drill-down dialog state
  const [ddOpen, setDdOpen]         = useState(false);
  const [ddTitle, setDdTitle]       = useState('');
  const [ddDesc, setDdDesc]         = useState('');
  const [ddType, setDdType]         = useState('');
  const [ddRecords, setDdRecords]   = useState([]);
  const [ddLoading, setDdLoading]   = useState(false);
  const [ddTotalRevenue, setDdTotalRevenue] = useState(null);
  const [ddTotalBilling, setDdTotalBilling] = useState(null);

  const openDrilldown = useCallback(async (type, title, description) => {
    setDdTitle(title);
    setDdDesc(description);
    setDdType(type);
    setDdRecords([]);
    setDdTotalRevenue(null);
    setDdTotalBilling(null);
    setDdOpen(true);
    setDdLoading(true);
    try {
      const params = { type, period };
      if (period === 'custom') { params.from_date = fromDate; params.to_date = toDate; }
      const result = await reportsService.getPerformanceDrilldown(params);
      setDdRecords(result.records || []);
      if (result.total_revenue != null) setDdTotalRevenue(result.total_revenue);
      if (result.total_billing != null) setDdTotalBilling(result.total_billing);
    } catch (err) {
      console.error('[PC] Drill-down fetch failed:', err);
      setDdRecords([]);
    } finally {
      setDdLoading(false);
    }
  }, [period, fromDate, toDate]);

  // Access guard
  useEffect(() => {
    if (!isAdmin && !isScheduler) navigate('/home');
  }, [isAdmin, isScheduler, navigate]);

  const load = useCallback(async () => {
    if (period === 'custom' && (!fromDate || !toDate)) return;
    setLoading(true);
    setError(null);
    try {
      const params = { period };
      if (period === 'custom') { params.from_date = fromDate; params.to_date = toDate; }
      const overviewRes = await reportsService.getPerformanceOverview(params);
      setData(overviewRes);
      // current_month_forecast is now embedded in the performance-overview response
      setPulseData(overviewRes.current_month_forecast ? overviewRes : null);
    } catch {
      setError('Failed to load performance data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [period, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin && !isScheduler) return null;

  // Custom period: waiting for user to apply dates
  const awaitingCustom = period === 'custom' && (!fromDate || !toDate);

  // ── Derived chart data ──────────────────────────────────────────────────────
  const curMonthISO = currentMonthISO();

  const revenueChartData = (data?.revenue?.monthly_trend ?? []).map(m => ({
    rawMonth:  m.month,
    month:     fmtMonth(m.month),
    amount:    m.amount,
    job_count: m.job_count,
    isCurrent: m.month === curMonthISO,
  }));

  // FTA: last 6 months of the trend — switch source based on method
  const ftaTrendAll = ftaMethod === 'all'
    ? (data?.quality?.monthly_fta_all_trend ?? [])
    : (data?.quality?.monthly_fta_trend ?? []);
  const ftaChartData = ftaTrendAll.slice(-6).map(m => ({
    rawMonth: m.month,
    month:    fmtMonth(m.month),
    rate:     m.rate,
    total:    m.total_submitted,
    fta:      m.first_time_approvals,
    isCurrent: m.month === curMonthISO,
  }));

  // Backlog ageing buckets (cumulative → per-band)
  const backlog = data?.operations?.operational_backlog ?? 0;
  const a4  = data?.operations?.aged_4_plus  ?? 0;
  const a8  = data?.operations?.aged_8_plus  ?? 0;
  const a15 = data?.operations?.aged_15_plus ?? 0;
  const ageingBuckets = [
    { label: '2–3d',  count: backlog - a4, color: '#fbbf24', drillType: 'aged_0_3',  drillTitle: 'Overdue 2–3 Days'  },
    { label: '4–7d',  count: a4 - a8,      color: '#f97316', drillType: 'aged_4_7',  drillTitle: 'Overdue 4–7 Days'  },
    { label: '8–14d', count: a8 - a15,     color: '#ef4444', drillType: 'aged_8_14', drillTitle: 'Overdue 8–14 Days' },
    { label: '15+d',  count: a15,          color: '#991b1b', drillType: 'aged_15_plus', drillTitle: 'Overdue 15+ Days (Critical)' },
  ];

  // Period label for display
  const periodLabel = data
    ? `${format(parseISO(data.period.start), 'd MMM yyyy')} – ${format(parseISO(data.period.end), 'd MMM yyyy')}`
    : '';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="performance-centre-page">

      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-slate-600" />
            Performance Centre
          </h1>
          {periodLabel && (
            <p className="text-sm text-slate-500 mt-0.5" data-testid="period-label">
              {periodLabel}
              {data?.region_filter && (
                <span className="ml-2 text-xs font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  {data.region_filter}
                </span>
              )}
            </p>
          )}
        </div>

        <PeriodSelector
          period={period}
          setPeriod={p => { setPeriod(p); if (p !== 'custom') { setFromDate(''); setToDate(''); } }}
          pendingFrom={pendingFrom}
          setPendingFrom={setPendingFrom}
          pendingTo={pendingTo}
          setPendingTo={setPendingTo}
          onApply={() => { setFromDate(pendingFrom); setToDate(pendingTo); }}
        />
      </div>

      {/* Scheduled Insights link */}
      {isAdmin && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/reports/management-digest')}
            data-testid="scheduled-insights-btn"
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
          >
            <Bell className="h-4 w-4 mr-1.5" />
            Scheduled Insights
          </Button>
        </div>
      )}

      {awaitingCustom && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="awaiting-custom">
          <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
          Select a start and end date, then click Apply.
        </div>
      )}

      {/* Loading */}
      {!awaitingCustom && loading && (
        <div className="flex items-center justify-center py-16 gap-3 text-slate-400" data-testid="loading-state">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm">Loading performance data…</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600" data-testid="error-state">
          {error}
        </div>
      )}

      {/* Content */}
      {!loading && !error && data && (
        <div className="space-y-5">

          {/* ── 1. Revenue — Management view only ────────────────────────── */}
          {data?.revenue && (
          <Section
            id="revenue"
            icon={TrendingUp}
            title="Revenue"
            accent="emerald"
            links={[
              { to: '/reports', label: 'Revenue Forecast' },
              { to: '/reports/forecast-variance', label: 'Forecast Variance' },
            ]}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data?.current_month_forecast && (
                <KPITile
                  label={`Forecast — ${format(new Date(), 'MMM yyyy')}`}
                  value={formatCurrency(data.current_month_forecast?.amount ?? 0, 0)}
                  sub={`${data.current_month_forecast?.job_count ?? 0} jobs this month`}
                  color="emerald"
                  testId="kpi-forecast-revenue"
                  tooltip={`Includes all Scheduled and Completed jobs whose dates overlap with ${format(new Date(), 'MMMM yyyy')}. The number may be higher than the live schedule count, which only shows Scheduled-status jobs.`}
                  onClick={() => openDrilldown('forecast_revenue', `Forecast Revenue — ${format(new Date(), 'MMMM yyyy')}`, `All Scheduled and Completed jobs overlapping ${format(new Date(), 'MMMM yyyy')} that contribute to this month's forecast.`)}
                />
              )}
              <KPITile
                label={`Completed — ${period === 'ytd' ? 'YTD' : period === 'last_12m' ? 'Last 12M' : 'Period'}`}
                value={formatCurrency(data.revenue.completed_ytd, 0)}
                sub={data.prior_period ? (
                  <span className="inline-flex items-center gap-1">
                    vs prior <ChangeChip pct={data.revenue.ytd_vs_prior_pct} />
                  </span>
                ) : undefined}
                color="emerald"
                testId="kpi-completed-revenue"
              />
              {data.prior_period && (
                <KPITile
                  label="Prior Equivalent"
                  value={formatCurrency(data.revenue.completed_prior_ytd, 0)}
                  sub={`${format(parseISO(data.prior_period.start), 'd MMM')} – ${format(parseISO(data.prior_period.end), 'd MMM yyyy')}`}
                  color="slate"
                  testId="kpi-prior-revenue"
                />
              )}
            </div>

            {revenueChartData.length > 0 ? (
              <div data-testid="revenue-chart">
                <p className="text-xs font-medium text-slate-500 mb-2">
                  Completed Revenue — Monthly Trend
                  <span className="ml-2 text-[10px] font-normal text-amber-600">
                    ■ = In Progress (current month)
                  </span>
                </p>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueChartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                        width={36}
                      />
                      <Tooltip content={<RevenueTooltip formatCurrency={formatCurrency} />} />
                      <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                        {revenueChartData.map((d, i) => (
                          <Cell
                            key={i}
                            fill={d.isCurrent ? '#f59e0b' : '#10b981'}
                            opacity={d.amount === 0 ? 0.3 : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <EmptyChart label="No completed revenue data for this period" />
            )}
          </Section>
          )}

          {/* ── 2. Operations ────────────────────────────────────────────────── */}
          <Section
            id="operations"
            icon={CheckCircle}
            title="Operations"
            accent="blue"
            links={[
              { to: '/schedule', label: 'Job Tracker' },
            ]}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPITile
                label={`Completed Jobs — ${period === 'ytd' ? 'YTD' : period === 'last_12m' ? 'Last 12M' : 'Period'}`}
                value={data.operations.completed_jobs.toLocaleString()}
                sub={data.prior_period
                  ? `Prior: ${data.operations.completed_jobs_prior.toLocaleString()}`
                  : undefined}
                color="blue"
                testId="kpi-completed-jobs"
                onClick={() => openDrilldown('completed_jobs', 'Completed Jobs', `Jobs completed in the selected period (${period === 'ytd' ? 'Year to Date' : period === 'last_12m' ? 'Last 12 months' : 'Custom period'}).`)}
              />
              <KPITile
                label="Operational Backlog"
                value={data.operations.operational_backlog}
                sub="Overdue scheduled jobs"
                color={data.operations.operational_backlog > 0 ? 'orange' : 'slate'}
                testId="kpi-operational-backlog"
                onClick={() => openDrilldown('backlog', 'Operational Backlog', 'Scheduled jobs with a past-due date that have not yet been completed.')}
              />
              <KPITile
                label="Overdue 8+ Days"
                value={data.operations.aged_8_plus}
                sub="Needs prompt action"
                color={data.operations.aged_8_plus > 0 ? 'red' : 'slate'}
                testId="kpi-aged-8-plus"
                onClick={() => openDrilldown('aged_8_plus', 'Overdue 8+ Days', 'Scheduled jobs that have been overdue for 8 or more days and require prompt action.')}
              />
              <KPITile
                label="Overdue 15+ Days"
                value={data.operations.aged_15_plus}
                sub="Critical — action required"
                color={data.operations.aged_15_plus > 0 ? 'red' : 'slate'}
                testId="kpi-aged-15-plus"
                onClick={() => openDrilldown('aged_15_plus', 'Overdue 15+ Days', 'Critical: scheduled jobs that have been overdue for 15 or more days.')}
              />
            </div>

            {/* Backlog Ageing */}
            <div data-testid="backlog-ageing">
              <p className="text-xs font-medium text-slate-500 mb-2">Backlog Ageing</p>
              {backlog === 0 ? (
                <p className="text-sm text-slate-400 py-2">No overdue jobs — backlog is clear.</p>
              ) : (
                <div className="space-y-2">
                  {ageingBuckets.map(b => (
                    <AgeingRow
                      key={b.label}
                      label={b.label}
                      count={b.count}
                      total={backlog}
                      color={b.color}
                      onClick={() => openDrilldown(b.drillType, b.drillTitle, `Scheduled jobs overdue by ${b.label}.`)}
                    />
                  ))}
                  <p className="text-[10px] text-slate-400 pt-1">
                    Total backlog: {backlog} job{backlog !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </Section>

          <Section
            id="compliance"
            icon={Building2}
            title="Compliance"
            accent="orange"
            links={[{ to: '/reports/audit-frequency-compliance', label: 'Audit Frequency Compliance' }]}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KPITile
                label="Overdue Sites"
                value={data.compliance.total_overdue_sites}
                sub="Past audit frequency"
                color={data.compliance.total_overdue_sites > 0 ? 'orange' : 'slate'}
                testId="kpi-overdue-sites"
                tooltip="Number of active sites that have exceeded their scheduled audit frequency interval."
                onClick={() => openDrilldown('overdue_sites', 'Overdue Sites', 'Sites that have exceeded their expected audit frequency.')}
              />
              {data.compliance.billing_at_risk != null && (
                <KPITile
                  label="Billing at Risk"
                  value={formatCurrency(data.compliance.billing_at_risk, 0)}
                  sub="From overdue sites"
                  color={data.compliance.billing_at_risk > 0 ? 'orange' : 'slate'}
                  testId="kpi-billing-at-risk"
                  tooltip="Combined billing value of all overdue sites. This is the total at-risk revenue if these sites are not audited."
                  onClick={() => openDrilldown('billing_at_risk', 'Billing at Risk', 'Overdue sites and their individual billing value. Total represents the revenue at risk if these sites remain unaudited.')}
                />
              )}
              <KPITile
                label="No Future Audit — Count"
                value={data.compliance.overdue_no_future}
                sub="Overdue sites, no audit booked"
                color={data.compliance.overdue_no_future > 0 ? 'red' : 'slate'}
                testId="kpi-overdue-no-future"
                tooltip="Number of currently overdue sites that have NO future audit scheduled. These are the highest-risk sites."
                onClick={() => openDrilldown('overdue_no_future', 'Overdue Sites — No Future Audit', 'Sites that are overdue with no future audit currently scheduled.')}
              />
            </div>

            {data.compliance.total_overdue_sites === 0 && (
              <p className="text-sm text-emerald-600 font-medium" data-testid="compliance-all-clear">
                All sites are within their audit frequency — no overdue sites.
              </p>
            )}
          </Section>

          {/* ── 4. Quality ───────────────────────────────────────────────────── */}
          <Section
            id="quality"
            icon={Trophy}
            title="Quality"
            accent="indigo"
            links={[{ to: '/reports?report=preparation-quality', label: 'Preparation Quality' }]}
          >
            {/* ── Method toggle ──────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5" data-testid="fta-method-toggle">
                <button
                  onClick={() => setFtaMethod('formal')}
                  data-testid="fta-toggle-formal"
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    ftaMethod === 'formal'
                      ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Formal Review
                </button>
                <button
                  onClick={() => setFtaMethod('all')}
                  data-testid="fta-toggle-all"
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    ftaMethod === 'all'
                      ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  All Reports
                </button>
              </div>
              <p className="text-[11px] text-slate-400 leading-snug max-w-xs" data-testid="fta-method-note">
                {ftaMethod === 'formal'
                  ? 'Reports submitted through the formal submit \u2192 review \u2192 approve pipeline only. Use this to monitor your quality control process.'
                  : 'All published reports including direct-publish. Reflects your full output volume; use to check overall first-time acceptance.'}
              </p>
            </div>

            {/* ── KPI tiles — switch values by method ──────────────────────── */}
            {(() => {
              const rate    = ftaMethod === 'all' ? data.quality.fta_all_rate_current  : data.quality.fta_rate_current;
              const fta     = ftaMethod === 'all' ? data.quality.fta_all_approved_count : data.quality.fta_approved_count;
              const total   = ftaMethod === 'all' ? data.quality.fta_all_total          : data.quality.fta_total_submitted;
              const priorR  = ftaMethod === 'all' ? data.quality.fta_all_rate_prior     : data.quality.fta_rate_prior;
              const priorT  = ftaMethod === 'all' ? data.quality.fta_all_prior_total    : data.quality.fta_prior_total;
              const subLabel = ftaMethod === 'all' ? 'reports published' : 'formally submitted';
              const tipCurr  = ftaMethod === 'all'
                ? 'All published reports in the past 90 days. Includes formal review and direct-publish. Numerator = those with no return event.'
                : 'Reports submitted through the formal submit \u2192 review \u2192 approve workflow in the past 90 days.';
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <KPITile
                    label="FTA Rate — Rolling 90d"
                    value={`${rate}%`}
                    sub={`${fta}/${total} ${subLabel}`}
                    color={rate >= 85 ? 'indigo' : rate >= 70 ? 'orange' : 'red'}
                    testId="kpi-fta-current"
                    tooltip={tipCurr}
                  />
                  <KPITile
                    label="FTA Rate — Prior 90d"
                    value={`${priorR}%`}
                    sub={`${priorT} ${subLabel}`}
                    color="slate"
                    testId="kpi-fta-prior"
                    tooltip="FTA rate for the preceding 90-day window (91–181 days ago). Used to compute the change trend below."
                  />
                  {rate !== null && priorR !== null && total >= 5 && priorT >= 5 && (
                    <KPITile
                      label="FTA Change"
                      value={
                        <ChangeChip pct={
                          priorR > 0
                            ? Math.round((rate - priorR) * 10) / 10
                            : null
                        } />
                      }
                      sub="vs prior 90-day window"
                      color="slate"
                      testId="kpi-fta-change"
                    />
                  )}
                </div>
              );
            })()}

            {ftaChartData.length >= 2 ? (
              <div data-testid="fta-chart" className="mt-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                  <p className="text-xs font-medium text-slate-500">First-Time Approval — 6-Month Trend</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><svg className="w-3 h-3" viewBox="0 0 12 2"><line x1="0" y1="1" x2="12" y2="1" stroke="#6366f1" strokeWidth="2"/></svg> Rolling FTA rate</span>
                    {(ftaMethod === 'all' ? data.quality.fta_all_rate_prior : data.quality.fta_rate_prior) > 0 && (
                      <span className="flex items-center gap-1"><svg className="w-4 h-2" viewBox="0 0 16 4"><line x1="0" y1="2" x2="16" y2="2" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 2"/></svg> Prior 90d ({ftaMethod === 'all' ? data.quality.fta_all_rate_prior : data.quality.fta_rate_prior}%)</span>
                    )}
                    <span className="flex items-center gap-1"><circle cx="5" cy="5" r="3" fill="#f59e0b" className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-0.5" /> ● = In Progress</span>
                  </div>
                </div>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ftaChartData} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, 100]}
                        tickFormatter={v => `${v}%`}
                        width={36}
                      />
                      <Tooltip content={<FTATooltip />} />
                      {(ftaMethod === 'all' ? data.quality.fta_all_rate_prior : data.quality.fta_rate_prior) > 0 && (
                        <ReferenceLine
                          y={ftaMethod === 'all' ? data.quality.fta_all_rate_prior : data.quality.fta_rate_prior}
                          stroke="#94a3b8" strokeDasharray="4 2" strokeWidth={1.5}
                          label={{ value: `Prior: ${ftaMethod === 'all' ? data.quality.fta_all_rate_prior : data.quality.fta_rate_prior}%`, position: 'insideTopRight', fontSize: 9, fill: '#94a3b8', offset: 18 }}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey="rate"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          return payload.isCurrent
                            ? <circle key={cx} cx={cx} cy={cy} r={5} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                            : <circle key={cx} cx={cx} cy={cy} r={3} fill="#6366f1" stroke="#fff" strokeWidth={1.5} />;
                        }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <EmptyChart label="Not enough FTA data to show a trend for this period" />
            )}
          </Section>

        </div>
      )}

      {/* Performance Centre Drill-Down Dialog */}
      <PCDrilldownDialog
        open={ddOpen}
        onOpenChange={setDdOpen}
        title={ddTitle}
        description={ddDesc}
        type={ddType}
        records={ddRecords}
        loading={ddLoading}
        totalRevenue={ddTotalRevenue}
        totalBilling={ddTotalBilling}
      />
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-32 flex items-center justify-center text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-200">
      <div className="text-center">
        <BarChart3 className="h-6 w-6 mx-auto mb-1 opacity-40" />
        <p className="text-xs">{label}</p>
      </div>
    </div>
  );
}
